import { Dispatch } from 'react';
import { isEmpty } from 'lodash-es';

import { Action, AppState } from '@/state/AppStateContext';
import { faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faEnvelope, faFileCode, faFileLines, faImage } from "@fortawesome/free-solid-svg-icons";

import excel_logo from "@/assets/files/excel.png";
import html_logo from "@/assets/files/html.png";
import jpg_logo from "@/assets/files/jpg.png";
import json_logo from "@/assets/files/json.png";
import markdown_logo from "@/assets/files/markdown.png";
import outlook_logo from "@/assets/files/outlook.png";
import pdf_logo from "@/assets/files/pdf.png";
import png_logo from "@/assets/files/png.png";
import pptx_logo from "@/assets/files/pptx.png";
import python_logo from "@/assets/files/python.png";
import txt_logo from "@/assets/files/txt.png";
import word_logo from "@/assets/files/word.png";
import xml_logo from "@/assets/files/xml.png";

type AppContextType = { 
  state: AppState;
  dispatch: Dispatch<Action>;
} | undefined;

/**
 * File and image validation utilities
 */

export const FILE_UPLOAD_CONSTRAINTS = {
  MAX_FILES: 15,
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50 MB
  MAX_IMAGES: 1,
  MAX_IMAGE_SIZE: 20 * 1024 * 1024, // 20 MB
  MAX_ANTHROPIC_IMAGE_SIZE: 5 * 1024 * 1024, // 5 MB
  ALLOWED_FILE_TYPES: [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.csv',
    '.ppt', '.pptx', '.msg', '.json', '.xml', '.txt',
    '.htm', '.html', '.md', '.py', '.m', '.yml', '.yaml',
    '.toml', '.sh', '.bash', '.ps1', '.sql', '.js', '.ts',
    '.java', '.cpp', '.r', '.ipynb', '.eml', '.log', '.mat', '.pbit',
    '.ini', '.cfg', '.conf', '.properties',
    '.c', '.h', '.hpp', '.cs', '.v', '.sv', '.vhdl',
    '.tmdl', '.bim', '.tsv', '.xltx', '.xltm', '.xsd', '.bpmn',
    '.jpeg', '.jpg', '.png'
  ],
  ALLOWED_IMAGE_TYPES: ['.jpeg', '.jpg', '.png'],
  ALLOWED_FILE_MIME_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.ms-excel.template.macroEnabled.12',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-outlook',
    'image/jpeg',
    'image/png',
    'text/plain',
    'text/html',
    'text/csv',
    'text/tab-separated-values',
    'application/json',
    'application/xml',
    'text/xml',
    'text/markdown',
    'text/x-python',
    'text/x-matlab',
    'application/x-yaml',
    'application/javascript',
    'application/typescript',
    'message/rfc822',
    'application/x-matlab-data',
  ]
} as const;

// `.xlsm` used to be offered only when the tabular Code Interpreter flag was
// on, because without it the backend had no working read path for a macro
// workbook. The backend now converts every `.xlsm` upload to `.xlsx` before
// extraction regardless of that flag, so the gate would only hide a format
// that works. (`.xls` is unaffected: it is converted only when the flag is on,
// and reads through xlrd directly otherwise.)
export function getAllowedFileTypes(): readonly string[] {
  return FILE_UPLOAD_CONSTRAINTS.ALLOWED_FILE_TYPES;
}

export function getAllowedFileMimeTypes(): readonly string[] {
  return FILE_UPLOAD_CONSTRAINTS.ALLOWED_FILE_MIME_TYPES;
}

export interface FileValidationResult {
  valid: File[];
  oversized: File[];
  overLimit: File[];
}

/**
 * Validates files against size and count constraints
 * @param files - Array of files to validate
 * @param maxCount - Maximum number of files allowed
 * @param maxSize - Maximum file size in bytes
 * @returns Object containing valid files, oversized files, and files over the limit
 */
export function validateFiles(
  files: File[], 
  maxCount: number = FILE_UPLOAD_CONSTRAINTS.MAX_FILES, 
  maxSize: number = FILE_UPLOAD_CONSTRAINTS.MAX_FILE_SIZE
): FileValidationResult {
  // Split into valid and oversized
  const validFiles = files.filter(file => file.size <= maxSize);
  const oversized = files.filter(file => file.size > maxSize);

  // Enforce max count
  const allowed = validFiles.slice(0, maxCount);
  const overLimit = validFiles.slice(maxCount);

  return { valid: allowed, oversized, overLimit };
}

/**
 * Validates a single image against size constraints
 * @param file - Image file to validate
 * @param maxSize - Maximum image size in bytes
 * @returns true if valid, false otherwise
 */
export function validateImage(
  file: File,
  appStateContext: AppContextType
): boolean {
  return (appStateContext?.state.currentModel?.brand !== 'Anthropic' && file.size <= FILE_UPLOAD_CONSTRAINTS.MAX_IMAGE_SIZE) || (appStateContext?.state.currentModel?.brand === 'Anthropic' && file.size <= FILE_UPLOAD_CONSTRAINTS.MAX_ANTHROPIC_IMAGE_SIZE);
}

/**
 * Creates a new FileList from an array of files
 * @param files - Array of files
 * @returns DataTransfer with files
 */
export function createFileList(files: File[]): DataTransfer {
  const dataTransfer = new DataTransfer();
  files.forEach(file => dataTransfer.items.add(file));
  return dataTransfer;
}

/**
 * Shows an alert for file validation errors
 * @param result - File validation result
 */
export function showFileValidationErrors(result: FileValidationResult): void {
  if (result.oversized.length > 0) {
    const fileList = result.oversized.map(f => f.name).join(', ');
    alert(
      `The following file${result.oversized.length > 1 ? 's are' : ' is'} too large ` +
      `(max ${FILE_UPLOAD_CONSTRAINTS.MAX_FILE_SIZE / (1024 * 1024)} MB) and ${result.oversized.length > 1 ? 'have' : 'has'} been removed: ${fileList}`
    );
  }

  if (result.overLimit.length > 0) {
    alert(
      `You can only upload up to ${FILE_UPLOAD_CONSTRAINTS.MAX_FILES} files per conversation. ` +
      `${result.overLimit.length} file${result.overLimit.length > 1 ? 's have' : ' has'} been removed.`
    );
  }
}

/**
 * Shows an alert for image validation errors
 * @param maxSize - Maximum allowed image size
 */
export function showImageValidationError(appStateContext: AppContextType): void {
  const maxSize = appStateContext?.state.currentModel?.brand !== 'Anthropic' ? FILE_UPLOAD_CONSTRAINTS.MAX_IMAGE_SIZE : FILE_UPLOAD_CONSTRAINTS.MAX_ANTHROPIC_IMAGE_SIZE;
  alert(`File too large, max file size is ${maxSize / (1024 * 1024)}MB.`);
}

/**
 * Checks if any upload should be disabled based on app state
 * @param params - Object containing state checks
 * @returns true if file upload should be disabled
 */
export function isUploadDisabled (appStateContext: AppContextType): boolean {
  if (!appStateContext) return true;
  const { state } = appStateContext;
  const hasSelectedTool = !isEmpty(state.selectedTool);
  const fileCount = state.individualFileChat?.length || 0;
  const isMarketingCustomGPT = state.currentCustomGPT?.id === 'e7e6d46d-dd49-4a08-b02a-4499de9d540a';

  return (
    hasSelectedTool ||
    fileCount >= FILE_UPLOAD_CONSTRAINTS.MAX_FILES ||
    isMarketingCustomGPT
  );
}

/**
 * Checks if file upload should be disabled based on app state
 * @param params - Object containing state checks
 * @returns true if file upload should be disabled
 */
export function isFileUploadDisabled (appStateContext: AppContextType): boolean {
  if (!appStateContext) return true;
  const { state } = appStateContext;
  const hasSelectedTool = !isEmpty(state.selectedTool);
  const fileCount = state.individualFileChat?.length || 0;
  const isMarketingCustomGPT = state.currentCustomGPT?.id === 'e7e6d46d-dd49-4a08-b02a-4499de9d540a';
  
  return (
    hasSelectedTool ||
    fileCount >= FILE_UPLOAD_CONSTRAINTS.MAX_FILES ||
    isMarketingCustomGPT
  );
}

/**
 * Checks if image upload should be disabled based on app state
 * @param params - Object containing state checks
 * @returns true if image upload should be disabled
 */
export function isImageUploadDisabled (appStateContext: AppContextType): boolean {
  if (!appStateContext) return true;
  const { state } = appStateContext;
  const hasSelectedTool = !isEmpty(state.selectedTool);
  const fileCount = state.individualFileChat?.length || 0;
  
  return hasSelectedTool || fileCount >= FILE_UPLOAD_CONSTRAINTS.MAX_FILES;
}

/** 
 * Handle configuring file icon to use
 *
 * @param {string} filename - The citation's filename
**/
// export const getFileIcon = (filename: string): import("@fortawesome/fontawesome-svg-core").IconProp => {
export const getFileIcon = (filename: string) => {
  // Lower-cased: uploads keep the user's own spelling ("Report.XLSX"), and an
  // upper-case extension used to miss every branch and fall through to txt_logo.
  const fileExtension = filename.split('.').pop()?.toLowerCase();
  switch (fileExtension) {
    case 'pdf':
      return pdf_logo;
    case 'doc':
    case 'docx':
      return word_logo;
    case 'xls':
    case 'xlsx':
    case 'xlsm':
    case 'xltx':
    case 'xltm':
    case 'tsv':
      return excel_logo;
    case 'ppt':
    case 'pptx':
      return pptx_logo;
    case 'msg':
      return outlook_logo;
    case 'htm':
    case 'html':
      return html_logo;
    case 'py':
      return python_logo;
    case 'jpeg':
    case 'jpg':
      return jpg_logo;
    case 'png':
      return png_logo;
    case 'json':
      return json_logo;
    case 'md':
    case 'markdown':
      return markdown_logo;
    case 'xml':
      return xml_logo;
    default:
      return txt_logo;
  }
}
